import { useState } from "react";
import type { ContactInput, ContactRole } from "@career-ops-amplify/shared";
import { useCreateContact } from "@/hooks/useContacts";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";

const ROLES: ContactRole[] = [
  "recruiter",
  "hiring_manager",
  "interviewer",
  "referrer",
  "other",
];

interface ContactFormProps {
  companySlug: string;
  jobId?: string;
  onCreated?: () => void;
}

export function ContactForm({
  companySlug,
  jobId,
  onCreated,
}: ContactFormProps) {
  const createContact = useCreateContact();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [role, setRole] = useState<ContactRole>("recruiter");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    const input: ContactInput = {
      companySlug,
      name: name.trim(),
      email: email.trim() || undefined,
      linkedinUrl: linkedinUrl.trim() || undefined,
      role,
      title: title.trim() || undefined,
      notes: notes.trim() || undefined,
      jobIds: jobId ? [jobId] : undefined,
      source: "manual",
    };

    await createContact.mutateAsync(input);
    setName("");
    setEmail("");
    setLinkedinUrl("");
    setTitle("");
    setNotes("");
    onCreated?.();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Add contact
      </h4>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          type="text"
          placeholder="Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          required
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as ContactRole)}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r.replace("_", " ")}
            </option>
          ))}
        </select>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <input
          type="url"
          placeholder="LinkedIn URL"
          value={linkedinUrl}
          onChange={(e) => setLinkedinUrl(e.target.value)}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Title at company"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm sm:col-span-2"
        />
        <textarea
          placeholder="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm sm:col-span-2"
        />
      </div>
      <button
        type="submit"
        disabled={createContact.isPending || !name.trim()}
        className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
      >
        {createContact.isPending ? "Saving…" : "Save contact"}
      </button>
    </form>
  );
}

interface ContactListProps {
  contacts: Array<{
    id: string;
    name: string;
    email?: string;
    linkedinUrl?: string;
    role?: string;
    title?: string;
  }>;
  emptyMessage?: string;
}

export function ContactList({ contacts, emptyMessage }: ContactListProps) {
  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(contacts);

  if (contacts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {emptyMessage ?? "No contacts yet."}
      </p>
    );
  }

  return (
    <div>
      <div className="space-y-2">
        {pageItems.map((c) => (
        <div
          key={c.id}
          className="rounded-md border border-border bg-card px-3 py-2 text-sm"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium text-foreground">{c.name}</p>
              <p className="text-xs text-muted-foreground">
                {[c.role?.replace("_", " "), c.title].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  className="text-xs text-brand hover:underline"
                >
                  Email
                </a>
              )}
              {c.linkedinUrl && (
                <a
                  href={c.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand hover:underline"
                >
                  LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
      </div>
      <PaginationBar
        page={page}
        pageSize={pageSize}
        totalItems={contacts.length}
        totalPages={totalPages}
        showingFrom={showingFrom}
        showingTo={showingTo}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
